/**
 * Firestore-shaped client shim over the Supabase REST API.
 *
 * Phase 3 of the Firebase → Postgres migration. Components today import
 * helpers from `../firebase`:
 *
 *   import { db, collection, query, where, orderBy, limit,
 *            onSnapshot, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
 *            serverTimestamp, arrayUnion, increment, doc } from '../firebase';
 *
 * This module re-exports the SAME names with the SAME call signatures, but
 * routes every call to our Express server (`/api/:orgId/:table`) which talks
 * to Supabase Postgres. That lets us migrate one component at a time by simply
 * changing the import specifier — no query rewrite required.
 *
 * Realtime (`onSnapshot`) is currently implemented as short polling, which is
 * fine for an accounting app. It can be swapped for Supabase Realtime later
 * without touching call sites.
 *
 * NOTE: this is a bridge layer. Once all components have migrated, the
 * Firestore-shaped API can be deleted and components can call the typed
 * `apiClient` directly. Don't add new features here — mirror existing ones.
 */

const API_BASE = "/api";

// ---------------------------------------------------------------------------
// Auth token handling
// ---------------------------------------------------------------------------

const TOKEN_KEY = "app_auth_token";
const ORG_KEY = "selected_org_id";

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setAuthToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}
export function getCurrentOrgId(): string | null {
  return localStorage.getItem(ORG_KEY);
}
export function setCurrentOrgId(orgId: string | null) {
  if (orgId) localStorage.setItem(ORG_KEY, orgId);
  else localStorage.removeItem(ORG_KEY);
}

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  const orgId = getCurrentOrgId();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (orgId) headers["x-org-id"] = orgId;
  return headers;
}

// ---------------------------------------------------------------------------
// Reference objects (mirror Firestore's `db`, `collection`, `doc` shapes)
// ---------------------------------------------------------------------------

export const db = {
  /** Marker so `import { db }` keeps working unchanged in components. */
  __backend: "supabase" as const,
};

export interface CollectionRef {
  readonly __kind: "collection";
  readonly name: string;
}
export interface DocRef {
  readonly __kind: "doc";
  readonly collection: string;
  readonly id: string;
}
export interface QueryRef {
  readonly __kind: "query";
  readonly collection: string;
  readonly constraints: Constraint[];
}

export function collection(...args: any[]): CollectionRef {
  // Firestore allows collection(db) and collection(db, 'x', id, 'sub'). Be
  // permissive: pull the last string argument as the collection name and map
  // known sub-collection names to their flattened tables.
  const flatMap: Record<string, string> = {
    activities: 'license_activities',
    devices: 'license_devices',
  };
  const strings = args.filter((a) => typeof a === 'string');
  const lastName = strings[strings.length - 1] || '';
  const finalName = flatMap[lastName] || lastName;
  return { __kind: "collection", name: finalName };
}
export function doc(...args: any[]): DocRef {
  // Accept doc(db, 'col', 'id') and doc(db, 'col', 'id', 'sub', 'subId').
  const strings = args.filter((a) => typeof a === 'string');
  const collectionName = strings[0] || '';
  const flatMap: Record<string, string> = {
    activities: 'license_activities',
    devices: 'license_devices',
  };
  const id = strings[strings.length - 1] || '';
  const effectiveCollection = flatMap[strings[strings.length - 2]] || strings[strings.length - 2] || collectionName;
  return { __kind: "doc", collection: effectiveCollection || collectionName, id };
}

// ---------------------------------------------------------------------------
// Query constraints (mirror `where`, `orderBy`, `limit`)
// ---------------------------------------------------------------------------

export type Constraint =
  | { type: "where"; field: string; op: string; value: any }
  | { type: "orderBy"; field: string; dir: "asc" | "desc" }
  | { type: "limit"; n: number };

export function where(field: string, op: string, value: any): Constraint {
  return { type: "where", field, op, value };
}
export function orderBy(field: string, dir: "asc" | "desc" = "asc"): Constraint {
  return { type: "orderBy", field, dir };
}
export function limit(n: number): Constraint {
  return { type: "limit", n };
}
export function query(ref: CollectionRef | QueryRef, ...constraints: Constraint[]): QueryRef {
  const baseName = "__kind" in ref && ref.__kind === "query" ? ref.collection : (ref as CollectionRef).name;
  const baseConstraints = "__kind" in ref && ref.__kind === "query" ? ref.constraints : [];
  return { __kind: "query", collection: baseName, constraints: [...baseConstraints, ...constraints] };
}

function constraintsToParams(constraints: Constraint[]): URLSearchParams {
  const params = new URLSearchParams();
  for (const c of constraints) {
    if (c.type === "where") {
      params.append("where", `${c.field}|${c.op}|${encodeURIComponent(JSON.stringify(c.value))}`);
    } else if (c.type === "orderBy") {
      params.append("orderBy", `${c.field}|${c.dir}`);
    } else if (c.type === "limit") {
      params.append("limit", String(c.n));
    }
  }
  return params;
}

// ---------------------------------------------------------------------------
// Read/Write helpers
// ---------------------------------------------------------------------------

export interface DocSnapshot {
  id: string;
  ref: DocRef;
  exists: () => boolean;
  data: () => any;
}
export interface QuerySnapshot {
  docs: DocSnapshot[];
  empty: boolean;
  size: number;
  forEach: (cb: (doc: DocSnapshot) => void) => void;
}

function wrapDoc(row: any | null, collectionName: string = ""): DocSnapshot {
  if (!row) {
    return { id: "", ref: { __kind: "doc", collection: collectionName, id: "" }, exists: () => false, data: () => null };
  }
  const { id, ...rest } = row;
  return { id, ref: { __kind: "doc", collection: collectionName, id }, exists: () => true, data: () => rest };
}

export async function getDoc(ref: DocRef): Promise<DocSnapshot> {
  if (!ref.id) throw new Error("getDoc: doc reference has no id");
  const res = await fetch(`${API_BASE}/${ref.collection}/${ref.id}`, {
    headers: authHeaders(),
  });
  if (res.status === 404) return wrapDoc(null, ref.collection);
  if (!res.ok) throw new Error(await res.text());
  const row = await res.json();
  return wrapDoc(row, ref.collection);
}

export async function getDocs(q: QueryRef): Promise<QuerySnapshot> {
  const params = constraintsToParams(q.constraints);
  const url = `${API_BASE}/${q.collection}?${params.toString()}`;
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(await res.text());
  const rows: any[] = await res.json();
  const docs = rows.map((r) => wrapDoc(r, q.collection));
  return { docs, empty: rows.length === 0, size: rows.length, forEach: (cb: (d: DocSnapshot) => void) => docs.forEach(cb) };
}

export async function addDoc(ref: CollectionRef, data: any): Promise<{ id: string }> {
  const res = await fetch(`${API_BASE}/${ref.name}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(stripSentinels(data)),
  });
  if (!res.ok) throw new Error(await res.text());
  const row = await res.json();
  return { id: row.id };
}

export async function setDoc(ref: DocRef, data: any, _options?: { merge?: boolean }): Promise<void> {
  const res = await fetch(`${API_BASE}/${ref.collection}/${ref.id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(stripSentinels(data)),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function updateDoc(ref: DocRef, data: any): Promise<void> {
  const body = toUpdatePatch(stripSentinels(data));
  const res = await fetch(`${API_BASE}/${ref.collection}/${ref.id}`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function deleteDoc(ref: DocRef): Promise<void> {
  const res = await fetch(`${API_BASE}/${ref.collection}/${ref.id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
}

// ---------------------------------------------------------------------------
// Sentinel values (mirror serverTimestamp / arrayUnion / increment)
// ---------------------------------------------------------------------------

export const serverTimestamp = () => ({ __op: "serverTimestamp" });
export const arrayUnion = (...items: any[]) => ({ __op: "arrayUnion", items });
export const increment = (n: number) => ({ __op: "increment", n });

const SENTINEL = ["serverTimestamp", "arrayUnion", "increment"];

function stripSentinels(data: any): any {
  // The server translates these sentinels into SQL operations. We pass them
  // through as-is so addDoc/setDoc/send "the same intent" the Firestore paths
  // did. updateDoc takes a more compact patch form via toUpdatePatch below.
  return data;
}

function toUpdatePatch(data: any): any {
  // Compact patch: { arrayUnion: { field: [...] }, increment: { field: n } }
  const patch: any = {};
  const lists: any = {};
  const nums: any = {};
  for (const [k, v] of Object.entries<any>(data || {})) {
    if (v && typeof v === "object" && "__op" in v) {
      if (v.__op === "arrayUnion") lists[k] = v.items;
      else if (v.__op === "increment") nums[k] = v.n;
      else if (v.__op === "serverTimestamp") patch[k] = { __op: "serverTimestamp" };
    } else {
      patch[k] = v;
    }
  }
  if (Object.keys(lists).length) patch.__arrayUnion = lists;
  if (Object.keys(nums).length) patch.__increment = nums;
  return patch;
}

// ---------------------------------------------------------------------------
// Realtime (`onSnapshot`)
// ---------------------------------------------------------------------------

export interface Unsubscribe {
  (): void;
}

const POLL_INTERVAL_MS = 5000;

/**
 * Subscribe to a query. Returns an unsubscribe function, just like Firestore.
 *
 * Implementation: short polling. Phase 5 will swap this for Supabase Realtime
 * (postgres_changes channel) without changing the call signature.
 */
export function onSnapshot(
  ref: QueryRef | DocRef,
  onNext: (snapshot: any) => void,
  onError?: (error: Error) => void,
  _onCompletion?: () => void
): Unsubscribe {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const poll = async () => {
    if (cancelled) return;
    try {
      let snapshot: any;
      if ("__kind" in ref && ref.__kind === "query") {
        snapshot = await getDocs(ref);
      } else {
        snapshot = await getDoc(ref as DocRef);
      }
      if (!cancelled) onNext(snapshot);
    } catch (err: any) {
      if (!cancelled && onError) onError(err);
    } finally {
      if (!cancelled) timer = setTimeout(poll, POLL_INTERVAL_MS);
    }
  };

  poll();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
}

// ---------------------------------------------------------------------------
// Error handling parity with the old firebase.ts
// ---------------------------------------------------------------------------

export enum OperationType {
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
  LIST = "list",
  GET = "get",
  WRITE = "write",
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  // Use console.warn for graceful offline/migration handling
  console.warn(`API Error (${operationType}) at ${path}:`, error);
  // Re-throw so existing try/catch code paths behave the same as with Firestore.
  throw error instanceof Error ? error : new Error(String(error));
}
